import { ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddToListMode, SelectType } from "@/enum/list.enum";
import { useBulkAddToAnotherListMutation } from "@/pages/Private/redux/list/list.api";
import {
	AddToListConfirmModal,
	PendingTransfer,
} from "@/components/AddToListConfirm/AddToListConfirmModal";
import { transferResultMessage } from "@/utils/transferResultMessage";

import { BulkStatusScope, selectAllViewFilters } from "../utils/getBulkStatusScope";

const RESULT_MESSAGE_MS = 6000;

interface UseAddToAnotherListArgs {
	/** The list the selection is on. */
	listId: number;
	/** The table's selection, cleared after a move the server accepted (the leads are gone). */
	selection: {
		selectedItems: number[];
		selectType: SelectType;
		selectAll: boolean;
		toggleSelectAll: () => void;
		setSelectType: (selectType: SelectType) => void;
		setSelectedItems: (items: number[]) => void;
	};
	/** How many leads the selection covers, for the confirm question. */
	selectionCount: number;
	bulkStatusScope: BulkStatusScope;
	showMessage: (text: string, autoHideDuration?: number) => void;
}

export interface UseAddToAnotherListResult {
	/** The footer's "Add to List" picker wiring. */
	actions: {
		onSelectList: (list: { id: number; name: string }) => void;
		onCreateNew: (name: string) => void;
		isLoading: boolean;
	};
	/** The confirmation dialog, or null while nothing is picked. */
	modal: ReactNode;
}

/**
 * "Add to List" on a list's table: pick a destination, confirm copy or move, and report what the
 * server did. The report is built from the server's answer, never from the selection: the server
 * skips every lead the destination already holds, and naming the selection's size told WFBB that
 * 1,016 leads were added to a list that grew by 613 (PREC-4595).
 */
export const useAddToAnotherList = ({
	listId,
	selection,
	selectionCount,
	bulkStatusScope,
	showMessage,
}: UseAddToAnotherListArgs): UseAddToAnotherListResult => {
	const { t } = useTranslation();
	const [addToAnotherList, { isLoading }] = useBulkAddToAnotherListMutation();
	const [pending, setPending] = useState<PendingTransfer | null>(null);
	const [mode, setMode] = useState<AddToListMode>(AddToListMode.COPY);

	const { selectedItems, selectType } = selection;

	const close = useCallback(() => {
		setPending(null);
		setMode(AddToListMode.COPY);
	}, []);

	const onSelectList = useCallback((list: { id: number; name: string }) => {
		setPending({ type: "existing", listId: list.id, listName: list.name });
	}, []);

	const onCreateNew = useCallback((name: string) => {
		setPending({ type: "new", listName: name });
	}, []);

	const confirm = useCallback(async () => {
		if (!pending) {
			return;
		}

		// A picked list without an id must not fall through to "create a list of that name".
		if (pending.type === "existing" && !pending.listId) {
			showMessage(t("basics.error"));
			close();

			return;
		}

		const target =
			pending.type === "existing"
				? { targetListId: pending.listId }
				: { targetListName: pending.listName };

		try {
			const result = await addToAnotherList({
				listId,
				listPersonIds: selectedItems,
				selectType,
				mode,
				...selectAllViewFilters(bulkStatusScope),
				...target,
			}).unwrap();

			showMessage(
				transferResultMessage(t, {
					mode,
					listName: pending.listName,
					isNewList: pending.type === "new",
					result,
				}),
				RESULT_MESSAGE_MS
			);
			// A move takes the leads off this list, so the selection goes with them; select-all is
			// toggled off first, or the table's sync effect re-selects every row. A copy leaves the
			// leads here and keeps the user's selection.
			if (mode === AddToListMode.MOVE) {
				if (selection.selectAll) {
					selection.toggleSelectAll();
				}

				selection.setSelectType(SelectType.CURRENT_PAGE);
				selection.setSelectedItems([]);
			}
		} catch {
			showMessage(t("basics.error"));
		} finally {
			close();
		}
	}, [
		pending,
		addToAnotherList,
		listId,
		selectedItems,
		selectType,
		mode,
		bulkStatusScope,
		showMessage,
		selection,
		t,
		close,
	]);

	const modal = pending ? (
		<AddToListConfirmModal
			modeChoice={{ mode, onChange: setMode }}
			pending={pending}
			selectionCount={selectionCount}
			onClose={close}
			onConfirm={confirm}
		/>
	) : null;

	return { actions: { onSelectList, onCreateNew, isLoading }, modal };
};
